import { create } from 'zustand';
import { toast } from 'sonner';
import type { TaskBoard, TaskCard, TaskDetail, TaskEvent, TaskLabel } from '@shared/types/tasks';
import { compareRank } from '@shared/task-rank';

/**
 * Optimistic changes, kept beside the fetched data rather than written into it. Each change is
 * shown at once and re-applied on top of every refetch until one that started after the server
 * confirmed it has landed, so a slow Neon write never flickers back. A failed write just drops
 * its change (and says so).
 */

type Fields = Partial<Omit<TaskDetail, 'id' | 'events'>>;

interface Overlay {
  seq: number;
  taskId: string;
  fields?: Fields;
  /** For changes that aren't plain fields, like editing a chat message. */
  detail?: (task: TaskDetail) => TaskDetail;
  /** Added to a card's chat and file counts (deleting a message takes them away). */
  counts?: { chat: number; attachments: number };
  /**
   * Changes to other tasks' cards (and details) that follow from this one: a sub-task's Waiting on or
   * column changes its parent's roll-up (T-92). Each runs on the card as it currently is.
   */
  cards?: Array<{ taskId: string; card: (card: TaskCard) => TaskCard }>;
  settledAt: number | null;
}

interface NewTask {
  card: TaskCard;
  /** Detail fields known before the server answers (description). */
  seed: Partial<TaskDetail>;
  settledAt: number | null;
}

export interface PendingEvent {
  event: TaskEvent;
  status: 'sending' | 'failed';
  settledAt: number | null;
  retry?: () => void;
}

/** A label created, edited or deleted but not yet confirmed. */
interface LabelChange {
  seq: number;
  id: string;
  create?: TaskLabel;
  fields?: Partial<Omit<TaskLabel, 'id'>>;
  deleted?: boolean;
  settledAt: number | null;
}

export interface OptimisticState {
  overlays: Overlay[];
  newTasks: Record<string, NewTask>;
  events: Record<string, PendingEvent[]>;
  labels: LabelChange[];
}

export const useTaskOptimistic = create<OptimisticState>()(() => ({ overlays: [], newTasks: {}, events: {}, labels: [] }));

const set = useTaskOptimistic.setState;
const get = useTaskOptimistic.getState;
/** Confirmed changes are forgotten once any refetch has surely caught up. */
const FORGET_AFTER_MS = 30_000;
let seq = 0;

/** Refetch a task (and the boards) after its write has been confirmed. */
let refetch: (taskId: string) => void = () => {};
export function setOptimisticRefetch(fn: (taskId: string) => void): void {
  refetch = fn;
}

/** Still shown on top of data from a fetch that started at `fetchedFrom`? */
function stillPending(settledAt: number | null, fetchedFrom: number): boolean {
  return settledAt === null || settledAt > fetchedFrom;
}

type Result = { error?: string; code?: string };

/**
 * Change a task's fields (or its detail) at once; the request confirms or undoes it. A failure is
 * a toast unless `quiet` says the caller shows it itself (a stale description: its banner).
 */
export async function optimisticUpdate(
  taskId: string,
  change: { fields?: Fields; detail?: (task: TaskDetail) => TaskDetail; counts?: Overlay['counts']; cards?: Overlay['cards'] },
  request: Promise<Result>,
  options: { quiet?: (res: Result) => boolean } = {}
): Promise<boolean> {
  const overlay: Overlay = { seq: ++seq, taskId, ...change, settledAt: null };
  set((s) => ({ overlays: [...s.overlays, overlay] }));
  const res = await request;
  if (res.error) {
    if (!options.quiet?.(res)) toast.error(res.error);
    set((s) => ({ overlays: s.overlays.filter((o) => o.seq !== overlay.seq) }));
  } else {
    const settledAt = Date.now();
    set((s) => ({ overlays: s.overlays.map((o) => (o.seq === overlay.seq ? { ...o, settledAt } : o)) }));
    setTimeout(() => set((s) => ({ overlays: s.overlays.filter((o) => o.seq !== overlay.seq) })), FORGET_AFTER_MS);
  }
  refetch(taskId);
  // The other cards it changed (a sub-task's parent, T-92) are the server's word again too.
  for (const id of new Set((change.cards ?? []).map((c) => c.taskId))) if (id !== taskId) refetch(id);
  return !res.error;
}

/** Show a new task (card and detail) straight away; it disappears again if creating fails. */
export async function optimisticCreate(card: TaskCard, seed: Partial<TaskDetail>, request: Promise<Result>): Promise<boolean> {
  set((s) => ({ newTasks: { ...s.newTasks, [card.id]: { card, seed, settledAt: null } } }));
  const res = await request;
  if (res.error) {
    toast.error(res.error);
    set((s) => {
      const { [card.id]: _drop, ...rest } = s.newTasks;
      return { newTasks: rest };
    });
  } else {
    const settledAt = Date.now();
    set((s) => ({ newTasks: { ...s.newTasks, [card.id]: { card, seed, settledAt } } }));
    setTimeout(
      () =>
        set((s) => {
          const { [card.id]: _drop, ...rest } = s.newTasks;
          return { newTasks: rest };
        }),
      FORGET_AFTER_MS
    );
  }
  refetch(card.id);
  return !res.error;
}

/** Create, edit or delete a label at once; the request confirms it or undoes it. */
export async function optimisticLabel(
  change: Omit<LabelChange, 'seq' | 'settledAt'>,
  request: Promise<Result>
): Promise<boolean> {
  const entry: LabelChange = { ...change, seq: ++seq, settledAt: null };
  set((s) => ({ labels: [...s.labels, entry] }));
  const res = await request;
  if (res.error) {
    toast.error(res.error);
    set((s) => ({ labels: s.labels.filter((l) => l.seq !== entry.seq) }));
  } else {
    const settledAt = Date.now();
    set((s) => ({ labels: s.labels.map((l) => (l.seq === entry.seq ? { ...l, settledAt } : l)) }));
    setTimeout(() => set((s) => ({ labels: s.labels.filter((l) => l.seq !== entry.seq) })), FORGET_AFTER_MS);
  }
  refetch('');
  return !res.error;
}

function replaceEvent(taskId: string, eventId: string, update: (p: PendingEvent) => PendingEvent | null): void {
  set((s) => ({
    events: {
      ...s.events,
      [taskId]: (s.events[taskId] ?? []).flatMap((p) => {
        if (p.event.id !== eventId) return [p];
        const next = update(p);
        return next ? [next] : [];
      }),
    },
  }));
}

/**
 * Show a chat message at once. `send` must save it with the same id; on failure the message
 * stays, marked with Retry.
 */
export function optimisticEvent(taskId: string, event: TaskEvent, send: () => Promise<Result>): void {
  const attempt = async () => {
    replaceEvent(taskId, event.id, (p) => ({ ...p, status: 'sending' }));
    const res = await send();
    if (res.error) {
      replaceEvent(taskId, event.id, (p) => ({ ...p, status: 'failed', retry: () => void attempt() }));
      toast.error(res.error);
      return;
    }
    replaceEvent(taskId, event.id, (p) => ({ ...p, settledAt: Date.now() }));
    setTimeout(() => replaceEvent(taskId, event.id, () => null), FORGET_AFTER_MS);
    refetch(taskId);
  };
  set((s) => ({ events: { ...s.events, [taskId]: [...(s.events[taskId] ?? []), { event, status: 'sending', settledAt: null }] } }));
  void attempt();
}

export function dismissEvent(taskId: string, eventId: string): void {
  replaceEvent(taskId, eventId, () => null);
}

/* Applying the changes ------------------------------------------------------ */

function applyFields<T extends TaskCard>(item: T, overlays: Overlay[], fetchedFrom: number): T {
  let next = item;
  for (const o of overlays) {
    if (!stillPending(o.settledAt, fetchedFrom)) continue;
    if (o.taskId === item.id && o.fields) next = { ...next, ...o.fields };
    for (const c of o.cards ?? []) if (c.taskId === item.id) next = { ...next, ...c.card(next) };
  }
  return next;
}

/** Labels with pending creates, edits and deletes applied. */
function applyLabels(labels: TaskLabel[], changes: LabelChange[], fetchedFrom: number): TaskLabel[] {
  let next = labels;
  for (const c of changes) {
    if (!stillPending(c.settledAt, fetchedFrom)) continue;
    if (c.create && !next.some((l) => l.id === c.id)) next = [...next, c.create];
    if (c.fields) next = next.map((l) => (l.id === c.id ? { ...l, ...c.fields } : l));
    if (c.deleted) next = next.filter((l) => l.id !== c.id);
  }
  return next;
}

/**
 * Chat and file counts on a card: messages still sending (or confirmed after this board was
 * fetched) are counted, and deleted ones taken off, before the server's counts catch up.
 */
function applyCounts(card: TaskCard, state: OptimisticState, fetchedFrom: number): TaskCard {
  let chat = 0;
  let attachments = 0;
  for (const p of state.events[card.id] ?? []) {
    if (p.status === 'failed' || !stillPending(p.settledAt, fetchedFrom)) continue;
    chat += 1;
    attachments += p.event.attachments.length;
  }
  for (const o of state.overlays) {
    if (o.taskId !== card.id || !o.counts || !stillPending(o.settledAt, fetchedFrom)) continue;
    chat += o.counts.chat;
    attachments += o.counts.attachments;
  }
  if (!chat && !attachments) return card;
  return {
    ...card,
    chat_count: Math.max(0, card.chat_count + chat),
    attachment_count: Math.max(0, card.attachment_count + attachments),
  };
}

/**
 * A board with pending changes and new tasks applied (`inScope` decides where new tasks show).
 * `cardsFetchedFrom` has the cards refetched on their own since the board, with when.
 */
export function applyToBoard(
  board: TaskBoard,
  fetchedFrom: number,
  state: OptimisticState,
  inScope: (card: TaskCard) => boolean,
  cardsFetchedFrom: Record<string, number> = {}
): TaskBoard {
  if (
    !state.overlays.length &&
    !Object.keys(state.newTasks).length &&
    !Object.keys(state.events).length &&
    !state.labels.length
  ) {
    return board;
  }
  const labels = applyLabels(board.labels, state.labels, fetchedFrom);
  const have = new Set(board.tasks.map((t) => t.id));
  const added = Object.values(state.newTasks)
    // A card refetched since it was confirmed is the server's word on it (gone, archived or out of scope).
    .filter((n) => !have.has(n.card.id) && stillPending(n.settledAt, Math.max(fetchedFrom, cardsFetchedFrom[n.card.id] ?? 0)) && inScope(n.card))
    .map((n) => n.card);
  const order = board.columns.map((c) => c.id);
  const tasks = [...board.tasks, ...added]
    .map((t) => {
      const from = Math.max(fetchedFrom, cardsFetchedFrom[t.id] ?? 0);
      return applyCounts(applyFields(t, state.overlays, from), state, from);
    })
    .map((t) => (t.label_ids.every((id) => labels.some((l) => l.id === id)) ? t : { ...t, label_ids: t.label_ids.filter((id) => labels.some((l) => l.id === id)) }))
    .sort((a, b) => order.indexOf(a.column_id) - order.indexOf(b.column_id) || compareRank(a.rank, b.rank));
  return { ...board, labels, tasks };
}

/** A task's detail with pending changes and unsent messages applied. */
export function applyToDetail(task: TaskDetail, fetchedFrom: number, state: OptimisticState): TaskDetail {
  let next = applyFields(task, state.overlays, fetchedFrom);
  for (const o of state.overlays) {
    if (o.taskId === task.id && o.detail && stillPending(o.settledAt, fetchedFrom)) next = o.detail(next);
  }
  const pending = (state.events[task.id] ?? []).filter(
    (p) => !next.events.some((e) => e.id === p.event.id) && stillPending(p.settledAt, fetchedFrom)
  );
  return pending.length ? { ...next, events: [...next.events, ...pending.map((p) => p.event)] } : next;
}

/** Sending / failed state for a message still on its way. */
export function usePendingEvent(taskId: string, eventId: string): PendingEvent | undefined {
  return useTaskOptimistic((s) => s.events[taskId]?.find((p) => p.event.id === eventId && p.settledAt === null));
}

/** The optimistic seed for a task that's still being created, if any. */
export function newTaskSeed(taskId: string): NewTask | undefined {
  return get().newTasks[taskId];
}

/** How many task changes are still on their way to the server, and how many messages failed. */
export function usePendingSaves(): { saving: number; failed: number } {
  const saving = useTaskOptimistic(
    (s) =>
      s.overlays.filter((o) => o.settledAt === null).length +
      Object.values(s.newTasks).filter((n) => n.settledAt === null).length +
      Object.values(s.events).flat().filter((p) => p.status === 'sending').length
  );
  const failed = useTaskOptimistic((s) => Object.values(s.events).flat().filter((p) => p.status === 'failed').length);
  return { saving, failed };
}
